
import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, FolderPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useProjectManagement, Project, ProjectFormData } from '@/hooks/useProjectManagement';
import { ProjectForm } from './ProjectForm';
import { ProjectsList } from './ProjectsList';
import { useToast } from '@/hooks/use-toast';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

export const ProjectManagement: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('list');
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'completed' | 'on-hold'>('active');
  const [showCompleted, setShowCompleted] = useState(false);

  const {
    projects,
    loading,
    submitting,
    createProject,
    updateProject,
    deleteProject,
  } = useProjectManagement();

  const handleCreateProject = async (data: ProjectFormData) => {
    try {
      await createProject(data);
      setActiveTab('list');
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleUpdateProject = async (data: ProjectFormData) => {
    if (!editingProject) return;
    
    try {
      await updateProject(editingProject.id, data);
      setEditingProject(null);
      setActiveTab('list');
    } catch (error) {
      // Error handled in hook
    }
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setActiveTab('form');
  };

  const handleDeleteProject = async (projectId: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) {
      await deleteProject(projectId);
    }
  };

  const handleCancelEdit = () => {
    setEditingProject(null);
    setActiveTab('list');
  };

  const filteredProjects = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return projects.filter((p) => {
      const matchesStatus = statusFilter === 'all'
        || p.status === statusFilter
        || (statusFilter === 'active' && showCompleted && p.status === 'completed');
      const text = `${p.name} ${p.client_name ?? ''} ${p.description ?? ''}`.toLowerCase();
      const matchesQuery = query === '' || text.includes(query);
      return matchesStatus && matchesQuery;
    });
  }, [projects, searchQuery, statusFilter, showCompleted]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-orange-50 p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              size="icon"
              onClick={() => navigate('/manager')}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des Projets</h1>
              <p className="text-muted-foreground">Créer et gérer les projets de l'entreprise</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <FolderPlus className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <div className="text-2xl font-bold">{projects.length}</div>
              <div className="text-sm text-muted-foreground">Total projets</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">
                {projects.filter(p => p.status === 'active').length}
              </div>
              <div className="text-sm text-muted-foreground">Projets actifs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-blue-600">
                {projects.filter(p => p.status === 'completed').length}
              </div>
              <div className="text-sm text-muted-foreground">Projets terminés</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {projects.filter(p => p.status === 'on-hold').length}
              </div>
              <div className="text-sm text-muted-foreground">En pause</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <TabsList>
              <TabsTrigger value="list">Liste des projets</TabsTrigger>
              <TabsTrigger value="form">
                {editingProject ? 'Modifier le projet' : 'Nouveau projet'}
              </TabsTrigger>
            </TabsList>
            
            {activeTab === 'list' && (
              <div className="flex items-center gap-2">
                <Input
                  placeholder="Rechercher (nom, client, description)"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-56"
                />
                <div className="flex items-center gap-2 px-2">
                  <Switch
                    checked={showCompleted}
                    onCheckedChange={setShowCompleted}
                  />
                  <span className="text-sm text-muted-foreground">
                    Afficher aussi les projets terminés
                  </span>
                </div>
                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as any)}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Statut" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous</SelectItem>
                    <SelectItem value="active">Actif</SelectItem>
                    <SelectItem value="completed">Terminé</SelectItem>
                    <SelectItem value="on-hold">En pause</SelectItem>
                  </SelectContent>
                </Select>
                <Button onClick={() => setActiveTab('form')}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nouveau projet
                </Button>
              </div>
            )}
          </div>

          <TabsContent value="list" className="mt-6">
            <ProjectsList
              projects={filteredProjects}
              onEdit={handleEditProject}
              onDelete={handleDeleteProject}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="form" className="mt-6">
            <ProjectForm
              onSubmit={editingProject ? handleUpdateProject : handleCreateProject}
              initialData={editingProject}
              isSubmitting={submitting}
              onCancel={editingProject ? handleCancelEdit : undefined}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
